import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Calendar, MapPin, Clock, User, Loader2 } from 'lucide-react';
import api from '../../lib/api';

const MyClassesTab = ({ children }) => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyClasses();
  }, []);

  const fetchMyClasses = async () => {
    try {
      // Get bookings for this parent's children - includes schedule info
      const res = await api.get('/auth/my-bookings');
      
      // Group by class
      const classMap = {};
      for (const booking of res.data || []) {
        const classId = booking.class_id;
        if (!classMap[classId]) {
          // Use embedded schedule data from the booking
          const schedule = booking.schedule;
          
          classMap[classId] = {
            id: classId,
            name: booking.class_name || 'Unknown Class',
            student_name: booking.student_name,
            day: schedule?.day_of_week,  // Will be a number 0-6
            time: schedule?.start_time ? `${schedule.start_time} - ${schedule.end_time}` : 'TBA',
            location: schedule?.room_name || booking.location || 'TBA',
            status: booking.status,
            payment_status: booking.payment_status
          };
        }
      }
      
      setClasses(Object.values(classMap));
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDayName = (day) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    if (day === null || day === undefined) return 'TBA';
    if (typeof day === 'number' && day >= 0 && day <= 6) return days[day];
    if (typeof day === 'string' && !isNaN(parseInt(day))) {
      const num = parseInt(day);
      if (num >= 0 && num <= 6) return days[num];
    }
    return day || 'TBA';
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (status === 'confirmed' && paymentStatus === 'paid') {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Paid</Badge>;
    }
    if (paymentStatus === 'awaiting_authorization') {
      return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Payment Pending</Badge>;
    }
    if (status === 'cancelled') {
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Cancelled</Badge>;
    }
    return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">{status}</Badge>;
  };

  if (loading) {
    return (
      <Card className="bg-[#111] border-[#333]">
        <CardContent className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF007F] mx-auto mb-4" />
          <p className="text-white/50">Loading your classes...</p>
        </CardContent>
      </Card>
    );
  }

  if (classes.length === 0) {
    return (
      <Card className="bg-[#111] border-[#333]">
        <CardContent className="p-8 text-center">
          <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <h3 className="font-heading text-lg font-semibold mb-2 text-white">No classes yet</h3>
          <p className="text-white/50 mb-4">You haven't enrolled in any classes yet.</p>
          <Button 
            onClick={() => window.location.href = '/classes'}
            className="bg-[#FF007F] hover:bg-[#D6006A]"
          >
            Browse Classes
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="my-classes-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white">My Classes</h2>
        <Badge className="bg-[#FF007F]/20 text-[#FF007F] border-[#FF007F]/30">
          {classes.length} class{classes.length !== 1 ? 'es' : ''}
        </Badge>
      </div>

      <div className="grid gap-4">
        {classes.map((cls) => (
          <Card key={cls.id} className="bg-[#111] border-[#333] hover:border-[#FF007F]/50 transition-colors">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="font-heading text-lg font-semibold text-white">
                      {cls.name}
                    </h3>
                    {getStatusBadge(cls.status, cls.payment_status)}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-white/70">
                      <User className="w-4 h-4 text-[#FF007F]" />
                      <span>{cls.student_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/70">
                      <Calendar className="w-4 h-4 text-[#7000FF]" />
                      <span>{getDayName(cls.day)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/70">
                      <Clock className="w-4 h-4 text-green-400" />
                      <span>{cls.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/70 md:col-span-3">
                      <MapPin className="w-4 h-4 text-orange-400" />
                      <span>{cls.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default MyClassesTab;
